import type { ReactNode } from 'react'

import { isOffline, needsUpgrade } from '@/shared/api'
import { useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'

import { Alert } from './Alert'
import { Empty } from './Empty'
import { Skeleton } from './Skeleton'
import { Text } from './Text'

export function errorTitleKey(error: unknown): TranslationKey {
  if (isOffline(error)) {
    return 'error.offline.title'
  }

  return needsUpgrade(error) ? 'error.quota.title' : 'error.generic.title'
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown
  onRetry?: (() => void) | undefined
}) {
  const t = useTranslate()
  const reason = error instanceof Error ? error.message : undefined

  return (
    <Alert
      status="danger"
      title={t(errorTitleKey(error))}
      {...(onRetry ? { action: { label: t('app.retry'), onClick: onRetry } } : {})}
    >
      {reason ? (
        <Text role="code" tone="muted" measure>
          {reason}
        </Text>
      ) : null}
    </Alert>
  )
}

export function StateBlock({
  pending,
  error,
  onRetry,
  skeleton,
  empty,
  children,
}: {
  pending: boolean
  error?: unknown
  onRetry?: (() => void) | undefined
  skeleton?: ReactNode
  empty?: { title?: string; body: string } | undefined
  children: ReactNode
}) {
  if (pending) {
    return skeleton ?? <Skeleton />
  }

  if (error !== undefined && error !== null) {
    return <ErrorState error={error} onRetry={onRetry} />
  }

  if (empty) {
    return <Empty {...empty} />
  }

  return children
}
